import { Input } from "@/components/ui/input";
import { fmtNum } from "@/lib/helpers";

const good = "bg-emerald-500/15 text-emerald-300";
const bad = "bg-red-500/15 text-red-300";
const th = "px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-slate-400 font-semibold";
const td = "px-3 py-2.5 border-t border-slate-800/80";

function ResultCells({ m, rowSpan, testKey }) {
  return (
    <td rowSpan={rowSpan} className={`${td} text-center font-display font-bold text-lg border-l border-slate-800 ${m.target_qty ? (m.hgo >= 100 ? good : bad) : "text-slate-600"}`} data-testid={`hgo-${testKey}`}>
      {m.target_qty ? `%${m.hgo}` : "—"}
    </td>
  );
}

function ForecastCells({ m, rowSpan, testKey }) {
  return (
    <>
      <td rowSpan={rowSpan} className={`${td} text-center font-display font-bold text-lg ${m.target_qty ? (m.ok ? good : bad) : "text-slate-600"}`} data-testid={`fhgo-${testKey}`}>
        {m.target_qty ? `%${m.forecast_hgo}` : "—"}
      </td>
      <td rowSpan={rowSpan} className={`${td} text-center font-bold border-l border-slate-800 ${m.target_qty ? (m.ok ? good : bad) : "text-slate-600"}`} data-testid={`rule-${testKey}`}>
        {m.target_qty ? (m.ok ? "EVET" : "HAYIR") : "—"}
      </td>
      <td rowSpan={rowSpan} className={`${td} text-center font-display font-bold border-l border-slate-800 ${m.target_qty ? (m.remaining_qty > 0 ? "text-amber-300" : "text-emerald-300") : "text-slate-600"}`} data-testid={`daily-${testKey}`}>
        {m.target_qty ? (m.remaining_qty > 0 ? fmtNum(m.daily_needed) : "Tamam") : "—"}
      </td>
    </>
  );
}

function TargetCell({ line, editable, draft, setDraft }) {
  if (!editable) return <td className={`${td} text-right font-semibold tabular-nums`}>{fmtNum(line.target_qty)}</td>;
  return (
    <td className={`${td} text-right`}>
      <Input type="number" min="0" value={draft[line.item_id] ?? ""} placeholder="0"
             onChange={(e) => setDraft({ ...draft, [line.item_id]: e.target.value })}
             className="h-8 w-24 ml-auto text-right bg-slate-950/60 border-slate-700 tabular-nums" data-testid={`target-input-${line.item_id}`} />
    </td>
  );
}

function GroupRows({ g, editable, draft, setDraft }) {
  const combined = g.hgo_mode === "combined" && g.lines.length > 1;
  const span = g.lines.length + (combined ? 1 : 0);
  const rows = g.lines.map((l, i) => (
    <tr key={l.item_id} className="hover:bg-slate-800/20 transition-colors" data-testid={`hgo-row-${l.item_id}`}>
      {i === 0 && <td rowSpan={span} className={`${td} font-display font-bold text-base align-middle bg-slate-900/80 border-r border-slate-800`}>{g.name}</td>}
      <td className={`${td} text-sm`}>{l.name}</td>
      <TargetCell line={l} editable={editable} draft={draft} setDraft={setDraft} />
      <td className={`${td} text-right tabular-nums border-l border-slate-800`}>{fmtNum(l.sold_qty)}</td>
      {!combined && <ResultCells m={l} testKey={l.item_id} />}
      {combined && i === 0 && <ResultCells m={g.total} rowSpan={span} testKey={`g-${g.id}`} />}
      <td className={`${td} text-right tabular-nums border-l border-slate-800`}>{fmtNum(l.forecast_qty)}</td>
      {!combined && <ForecastCells m={l} testKey={l.item_id} />}
      {combined && i === 0 && <ForecastCells m={g.total} rowSpan={span} testKey={`g-${g.id}`} />}
    </tr>
  ));
  if (combined) {
    rows.push(
      <tr key="total" className="bg-slate-800/30">
        <td className={`${td} text-sm font-bold`}>Toplam</td>
        <td className={`${td} text-right font-bold tabular-nums`} data-testid={`group-total-target-${g.id}`}>{fmtNum(g.total.target_qty)}</td>
        <td className={`${td} text-right font-bold tabular-nums border-l border-slate-800`}>{fmtNum(g.total.sold_qty)}</td>
        <td className={`${td} text-right font-bold tabular-nums border-l border-slate-800`}>{fmtNum(g.total.forecast_qty)}</td>
      </tr>
    );
  }
  return rows;
}

export function HgoTable({ title, row, meta, editable, draft, setDraft }) {
  const groups = row.groups.filter((g) => g.lines.length);
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 overflow-hidden" data-testid="hgo-table">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse">
          <thead className="bg-slate-900/90">
            <tr>
              <th colSpan={3} className="px-4 py-3 text-left font-display text-xl font-bold text-slate-100" data-testid="hgo-table-title">{title}</th>
              <th className={`${th} border-l border-slate-800`}>Mevcut</th>
              <th rowSpan={2} className={`${th} border-l border-slate-800 w-24`}>HGO</th>
              <th colSpan={2} className={`${th} border-l border-slate-800`}>Ay Sonu Tahmini</th>
              <th rowSpan={2} className={`${th} border-l border-slate-800 w-28`}>%100 Kuralı</th>
              <th rowSpan={2} className={`${th} border-l border-slate-800 w-28`} title="Kalan günlere göre %100 için günde satılması gereken adet">Günlük Gereken</th>
            </tr>
            <tr>
              <th className={`${th} text-left w-40`}>Grup</th>
              <th className={`${th} text-left`}>Ürünler</th>
              <th className={`${th} text-right w-32`}>Hedef Adet</th>
              <th className={`${th} text-right border-l border-slate-800 w-36`}>Gerçekleşen Adet</th>
              <th className={`${th} text-right border-l border-slate-800 w-32`}>Tahmini Adet</th>
              <th className={`${th} w-24`}>HGO</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => <GroupRows key={g.id} g={g} editable={editable} draft={draft} setDraft={setDraft} />)}
            {groups.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-500 py-12 text-sm">Bu ay için hedef ayarı yok. "Hedef Ayarları" sekmesinden grup ve satır ekleyin.</td></tr>
            )}
          </tbody>
          {groups.length > 0 && (
            <tfoot className="bg-blue-600/10">
              <tr>
                <td colSpan={2} className={`${td} font-display font-bold`}>GENEL TOPLAM</td>
                <td className={`${td} text-right font-bold tabular-nums`} data-testid="hgo-grand-target">{fmtNum(row.total_target)}</td>
                <td className={`${td} text-right font-bold tabular-nums border-l border-slate-800`} data-testid="hgo-grand-sold">{fmtNum(row.total_sold)}</td>
                <td className={`${td} text-center font-bold border-l border-slate-800`} data-testid="hgo-grand-hgo">%{row.hgo}</td>
                <td className={`${td} text-right font-bold tabular-nums border-l border-slate-800`}>{fmtNum(row.forecast_qty)}</td>
                <td className={`${td} text-center font-bold`}>%{row.forecast_hgo}</td>
                <td className={`${td} text-center font-bold border-l border-slate-800 ${row.total_target ? (row.ok ? "text-emerald-300" : "text-red-300") : ""}`}>{row.total_target ? (row.ok ? "EVET" : "HAYIR") : "—"}</td>
                <td className={`${td} text-center font-bold border-l border-slate-800 text-amber-300`} data-testid="hgo-grand-daily">{row.total_target ? fmtNum(row.daily_needed) : "—"}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="flex flex-wrap gap-6 px-4 py-3 border-t border-slate-800 bg-slate-900/60 text-xs font-mono" data-testid="hgo-days">
        <span className="text-slate-400">Ay Toplam: <b className="text-slate-100">{meta.days_in_month}</b></span>
        <span className="text-slate-400">Rapor Gün: <b className="text-slate-100">{meta.days_elapsed}</b></span>
        <span className="text-slate-400">Kalan Gün: <b className="text-amber-300">{meta.days_left}</b></span>
      </div>
    </div>
  );
}
